<script lang="ts">
import CardView from '$components/Card/CardView.svelte';
import { drawExamCards, EXAM_PASS_THRESHOLD, type ExamResult, scoreExam } from '$lib/exam';
import type { Card } from '$types/card';

interface Props {
  cards: readonly Card[];
}

const { cards }: Props = $props();

let phase = $state<'setup' | 'run' | 'done'>('setup');
let count = $state(20);
let minutes = $state(30);
let examCards = $state<Card[]>([]);
let idx = $state(0);
let revealed = $state(false);
let marks = $state<boolean[]>([]);
let remainingSec = $state(0);
let result = $state<ExamResult | null>(null);

const current = $derived(examCards[idx] ?? null);
const wrongCards = $derived(examCards.filter((_c, i) => marks[i] === false));

function start(): void {
  examCards = drawExamCards(cards, count);
  marks = [];
  idx = 0;
  revealed = false;
  remainingSec = minutes * 60;
  result = null;
  phase = 'run';
}

function finish(padUnanswered: boolean): void {
  const finalMarks = padUnanswered
    ? [...marks, ...Array(Math.max(0, examCards.length - marks.length)).fill(false)]
    : [...marks];
  result = scoreExam(finalMarks);
  phase = 'done';
}

function mark(ok: boolean): void {
  marks = [...marks, ok];
  revealed = false;
  if (idx + 1 >= examCards.length) finish(false);
  else idx++;
}

$effect(() => {
  if (phase !== 'run') return;
  const t = setInterval(() => {
    remainingSec--;
    // Zeit abgelaufen: unbeantwortete Aufgaben zählen als falsch (Klausur-Realität)
    if (remainingSec <= 0) finish(true);
  }, 1000);
  return () => clearInterval(t);
});

function mmss(sec: number): string {
  const m = Math.floor(Math.max(0, sec) / 60);
  const s = Math.max(0, sec) % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function cardLabel(c: Card): string {
  const text = ('front' in c && c.front) || ('text' in c && c.text) || c.id;
  return text.replace(/\{\{c\d+::(.+?)\}\}/g, '$1').slice(0, 80);
}
</script>

<section class="exam">
  {#if phase === 'setup'}
    <div class="rule"><span class="rule-label">[ PROBE-KLAUSUR ]</span><span class="rule-line"></span></div>
    <p class="hint">
      Zieht zufällige Aufgaben aus den aktuell gefilterten Karten (ohne Vokabeln), läuft gegen die Uhr,
      <b>ohne</b> FSRS-Wertung. Unter {Math.round(EXAM_PASS_THRESHOLD * 100)} % → Thema in derselben Woche wiederholen.
    </p>
    <div class="setup-row">
      <label>aufgaben
        <select bind:value={count}>{#each [10, 20, 30] as n}<option value={n}>{n}</option>{/each}</select>
      </label>
      <label>zeit
        <select bind:value={minutes}>{#each [15, 30, 45] as n}<option value={n}>{n} min</option>{/each}</select>
      </label>
      <button type="button" class="btn-start" onclick={start} disabled={cards.length === 0}>▶ starten</button>
    </div>
  {:else if phase === 'run' && current}
    <div class="rule">
      <span class="rule-label">[ AUFGABE {marks.length + 1}/{examCards.length} ]</span>
      <span class="rule-line"></span>
      <span class="timer" class:timer-low={remainingSec < 120}>⏱ {mmss(remainingSec)}</span>
    </div>
    <CardView card={current} {revealed} position={marks.length + 1} total={examCards.length} />
    <div class="exam-actions">
      {#if !revealed}
        <button type="button" class="btn-start" onclick={() => (revealed = true)}>lösung zeigen</button>
      {:else}
        <button type="button" class="btn-ok" onclick={() => mark(true)}>✓ gewusst</button>
        <button type="button" class="btn-bad" onclick={() => mark(false)}>✗ nicht gewusst</button>
      {/if}
    </div>
  {:else if phase === 'done' && result}
    <div class="rule"><span class="rule-label">[ ERGEBNIS ]</span><span class="rule-line"></span></div>
    <div class="result" class:pass={result.passed} class:fail={!result.passed}>
      <div class="result-pct">{Math.round(result.pct * 100)} %</div>
      <div class="result-verdict">{result.passed ? 'BESTANDEN (≥ 85 %)' : 'UNTER 85 % — diese Woche wiederholen!'}</div>
      <div class="result-detail">{result.correct} von {result.total} Aufgaben gewusst</div>
    </div>
    {#if wrongCards.length}
      <div class="rule"><span class="rule-label">[ DRILL-LISTE ]</span><span class="rule-line"></span></div>
      <ul class="wrong-list">
        {#each wrongCards as c}<li><span class="wl-deck">{c.deck}</span> {cardLabel(c)}</li>{/each}
      </ul>
    {/if}
    <button type="button" class="btn-start" onclick={() => (phase = 'setup')}>↺ neue klausur</button>
  {/if}
</section>

<style>
  .exam { margin-top: 8px; }
  .rule {
    display: flex; align-items: center; gap: 10px;
    margin: 16px 0 8px;
    font-size: 11px; letter-spacing: 0.08em;
    color: var(--ink-3); text-transform: uppercase;
  }
  .rule-label { white-space: nowrap; font-weight: 600; }
  .rule-line { flex: 1; height: 0; border-top: 1px dashed var(--rule); opacity: 0.5; }
  .hint { font-size: 13px; color: var(--ink-2); }
  .timer { font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; }
  .timer-low { color: var(--r-again); }

  .setup-row { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; margin-top: 12px; }
  .setup-row label {
    display: flex; flex-direction: column; gap: 4px;
    font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted);
  }
  .setup-row select {
    border: 1px solid var(--rule); background: var(--paper); color: var(--ink);
    padding: 8px; font-family: var(--font-mono); font-size: 13px; border-radius: 0;
  }

  .btn-start, .btn-ok, .btn-bad {
    border: 2px solid var(--ink); background: var(--ink); color: var(--paper);
    padding: 10px 18px; font-family: var(--font-mono); font-size: 12px;
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; cursor: pointer;
  }
  .btn-start:hover { background: var(--accent); border-color: var(--accent); }
  .btn-start:disabled { opacity: 0.4; cursor: not-allowed; }
  .exam-actions { display: flex; gap: 8px; margin-top: 12px; }
  .exam-actions .btn-ok, .exam-actions .btn-bad { flex: 1; }
  .btn-ok { background: var(--r-good, #2e7d32); border-color: var(--r-good, #2e7d32); }
  .btn-bad { background: var(--r-again, #c62828); border-color: var(--r-again, #c62828); }

  .result { border: 2px solid var(--ink); padding: 20px; text-align: center; box-shadow: 6px 6px 0 var(--ink-3); }
  .result.pass { border-color: var(--r-good, #2e7d32); }
  .result.fail { border-color: var(--r-again, #c62828); }
  .result-pct { font-size: 56px; font-weight: 800; font-variant-numeric: tabular-nums; }
  .result.pass .result-pct { color: var(--r-good, #2e7d32); }
  .result.fail .result-pct { color: var(--r-again, #c62828); }
  .result-verdict { font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; margin-top: 4px; }
  .result-detail { font-size: 12px; color: var(--muted); margin-top: 6px; }

  .wrong-list { list-style: none; padding: 0; margin: 0 0 16px; font-size: 13px; }
  .wrong-list li { padding: 6px 0; border-bottom: 1px dashed var(--rule); color: var(--ink-2); }
  .wl-deck {
    display: inline-block; background: var(--paper-3); color: var(--ink-2);
    font-size: 10px; padding: 1px 6px; margin-right: 8px; letter-spacing: 0.04em;
  }
</style>
